import API_BASE from '../config';
import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

interface CreditRouteGuardProps {
  children: React.ReactNode;
  userRole: string;
}

// Pages that are always accessible (even with 0 credits)
const ALWAYS_ACCESSIBLE_PAGES = [
  '/dashboard',
  '/employees',
  '/user-groups',
  '/contracts',
  '/managers',
  '/settings'
];

const CreditRouteGuard: React.FC<CreditRouteGuardProps> = ({ children, userRole }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isChecking, setIsChecking] = useState(true);
  const [isRestricted, setIsRestricted] = useState(false);

  useEffect(() => {
    const checkCreditRestriction = async () => {
      // Only check for owners
      if (userRole !== 'OWNER') {
        setIsChecking(false);
        return;
      }

      // Session demo sandbox always has fake credits
      try {
        const stored = localStorage.getItem('user');
        if (stored) {
          const parsed = JSON.parse(stored);
          if (parsed?.isDemo) {
            setIsRestricted(false);
            setIsChecking(false);
            return;
          }
        }
      } catch { /* ignore */ }

      // Allow access to always accessible pages
      if (ALWAYS_ACCESSIBLE_PAGES.some(page => location.pathname.startsWith(page))) {
        setIsChecking(false);
        return;
      }

      try {
        const response = await fetch(API_BASE + '/api/credits/balance', {
          credentials: 'include',
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            // Restrict access if credits are 0 and not in free tier
            const shouldRestrict = !data.data.isFreeTier && data.data.balance <= 0;
            setIsRestricted(shouldRestrict);
            
            if (shouldRestrict) {
              // Redirect to dashboard with restriction message
              navigate('/dashboard');
            }
          }
        }
      } catch (error) {
        console.error('Error checking credit balance:', error);
      } finally {
        setIsChecking(false);
      }
    };

    checkCreditRestriction();
  }, [location.pathname, userRole, navigate]);

  if (isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return <>{children}</>;
};

export default CreditRouteGuard; 